// RUM (Grafana Faro). O SDK só é baixado para as sessões que serão de fato coletadas.
//
// Por que não `initializeFaro` direto no bundle principal: o SDK custa ~33 kB gz, e com
// amostragem em 30% isso significaria sete de cada dez visitantes baixando e executando
// um SDK cujo resultado é descartado — e são justamente esses visitantes cujo LCP o
// Google usa para ranquear. Sorteando antes do `import()`, quem não foi amostrado não
// paga nada.
//
// O preço da escolha é uma janela, entre o primeiro byte da página e a resolução do
// chunk, em que o Faro ainda não instalou os handlers dele. Os dois listeners abaixo
// cobrem essa janela e repassam o que aconteceu nela.

const url = process.env.REACT_APP_FARO_URL;

// Amostragem por variável de ambiente, e não constante, porque subir de 30% para 100%
// precisa ser trocar um valor no CI e reconstruir — sem PR nem revisão de código.
const taxa = Number.parseFloat(process.env.REACT_APP_FARO_SAMPLE_RATE ?? "0.3");
const amostragem = Number.isFinite(taxa) ? Math.min(Math.max(taxa, 0), 1) : 0.3;

// Sem `REACT_APP_FARO_URL` nada sobe: é o mesmo princípio de "instrumentação inerte" que
// o GTM já segue em `public/index.html`, e é o que mantém o dev local e a homologação
// livres de telemetria sem precisar de um segundo caminho de código.
const coletar = Boolean(url) && Math.random() < amostragem;

let faro = null;
const pendentes = [];

// O teto existe para o caso patológico de um laço de erro antes de o chunk resolver:
// sem ele, uma página quebrada encheria a memória com o próprio diagnóstico.
const enfileirar = (tipo, carga) => {
  if (pendentes.length < 50) pendentes.push([tipo, carga]);
};

const aoErro = (evento) => {
  const erro = evento?.error ?? evento?.reason;
  if (erro) enfileirar("erro", erro);
};

const soltarListeners = () => {
  window.removeEventListener("error", aoErro);
  window.removeEventListener("unhandledrejection", aoErro);
};

if (coletar) {
  window.addEventListener("error", aoErro);
  window.addEventListener("unhandledrejection", aoErro);

  import("@grafana/faro-web-sdk")
    .then(({ getWebInstrumentations, initializeFaro }) => {
      // Soltar antes de inicializar: a partir daqui quem captura é o Faro, e manter os
      // dois ativos registraria cada erro duas vezes.
      soltarListeners();

      faro = initializeFaro({
        url,
        app: {
          name: "biahflow-site",
          version: process.env.REACT_APP_VERSION || "dev",
          environment: process.env.REACT_APP_AMBIENTE || "local",
        },
        // `captureConsole` fica desligado: um `console.log` em laço vira volume de log
        // no free tier sem acrescentar nada ao diagnóstico que o erro já não diga.
        instrumentations: getWebInstrumentations({ captureConsole: false }),
        // `samplingRate: 1` porque a amostragem já foi decidida no sorteio acima —
        // chegar aqui significa que esta sessão foi escolhida. Repetir a taxa aplicaria
        // o descarte duas vezes e a coleta real cairia para 9% em vez de 30%.
        sessionTracking: { enabled: true, samplingRate: 1 },
      });

      for (const [tipo, carga] of pendentes) {
        if (tipo === "erro") {
          faro.api.pushError(carga);
        } else {
          faro.api.pushEvent(carga.nome, carga.atributos);
        }
      }
      pendentes.length = 0;
    })
    .catch(() => {
      // Falhar ao baixar o chunk (rede ruim, bloqueador) não pode derrubar o site —
      // telemetria é acessório. Solta os listeners para a fila não crescer sem dreno.
      soltarListeners();
      pendentes.length = 0;
    });
}

// Eventos de negócio. Antes de o chunk resolver, ficam na fila; se a sessão não foi
// sorteada, viram no-op.
//
// Note que o funil de captação não depende desta amostragem: o `lead_submit` também vai
// para o `dataLayer` do GTM em `Contact.jsx`, e lá é 100% das submissões.
export const pushEvent = (nome, atributos) => {
  if (faro) {
    faro.api.pushEvent(nome, atributos);
  } else if (coletar) {
    enfileirar("evento", { nome, atributos });
  }
};
